import { type FormEventHandler, type ReactElement, useState } from "react";
import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";
import PartnerAuthLayout from "@/Layouts/PartnerAuthLayout";
import { Button } from "@/Components/Common/Buttons";
import { CheckboxField, InputField } from "@/Components/Forms";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/Components/Overlays";
import { useTranslations } from "@/hooks/use-translations";
import { cn } from "@/lib/utils";
import { type PageProps } from "@/types";

interface LoginProps {
    status?: string | null;
    partnerAuth: App.Data.Settings.PartnerAuthSettingsData;
    /** Whether this tenant serves a public sign-up page (FDD §9.7). */
    signupEnabled: boolean;
}

type SignInMode = "password" | "magic";

/**
 * Partner sign-in (spec/partner-portal-plan.md §2.2, §14): password or
 * Microsoft, plus the magic-link tab when the tenant has switched it on —
 * the same email field, mailed a one-time link instead of checked against
 * a password.
 */
export default function Login({ status, partnerAuth, signupEnabled }: LoginProps): ReactElement {
    const { t } = useTranslations();
    const { flash } = usePage<PageProps & { flash: { error?: string } }>().props;
    const [mode, setMode] = useState<SignInMode>("password");
    const { data, setData, post, processing, errors, reset } = useForm({
        email: "",
        password: "",
        remember: false,
    });

    const microsoftEnabled = partnerAuth.microsoft_enabled;
    const passwordEnabled = partnerAuth.password_enabled;
    const magicLinkEnabled = partnerAuth.magic_link_enabled;
    const magicMode = magicLinkEnabled && (mode === "magic" || !passwordEnabled);

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        if (magicMode) {
            post(route("partner.login.magic.send"));

            return;
        }

        post(route("partner.login"), {
            onFinish: () => reset("password"),
        });
    };

    const modeButtonClass = (active: boolean): string =>
        cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground",
        );

    return (
        <PartnerAuthLayout title={t("partner.auth.welcome")} subtitle={t("partner.auth.welcome_subtitle")}>
            <Head title={t("auth.login_title")} />

            <div className="space-y-6">
                {status !== undefined && status !== null && status !== "" && (
                    <p className="text-sm font-medium text-status-success" role="status">
                        {status}
                    </p>
                )}

                {flash?.error !== undefined && flash.error !== "" && (
                    <p className="text-sm font-medium text-destructive" role="alert">
                        {flash.error}
                    </p>
                )}

                {passwordEnabled && (
                    <TooltipProvider>
                        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" role="tablist">
                            <button type="button" role="tab" aria-selected={!magicMode} className={modeButtonClass(!magicMode)} onClick={() => setMode("password")}>
                                {t("partner.auth.mode_password")}
                            </button>
                            {magicLinkEnabled ? (
                                <button type="button" role="tab" aria-selected={magicMode} className={modeButtonClass(magicMode)} onClick={() => setMode("magic")}>
                                    {t("partner.auth.mode_magic")}
                                </button>
                            ) : (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span tabIndex={0} className={cn(modeButtonClass(false), "cursor-not-allowed opacity-60")} aria-disabled="true">
                                            {t("partner.auth.mode_magic")}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{t("partner.auth.magic_off")}</TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    </TooltipProvider>
                )}

                {microsoftEnabled && (
                    <div className="space-y-4">
                        <a
                            href={route("partner.auth.microsoft.redirect")}
                            className="flex h-11 w-full items-center justify-center gap-3 rounded-md border border-border bg-card text-sm font-medium text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                            {/* Microsoft's own four-colour mark — brand asset, not theme colour. */}
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" aria-hidden="true">
                                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                            </svg>
                            {t("auth.sign_in_with_microsoft")}
                        </a>

                        {passwordEnabled && (
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-border" />
                                </div>
                                <div className="relative flex justify-center">
                                    <span className="bg-brand-form-surface px-3 text-xs text-muted-foreground">{t("partner.auth.or")}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {(passwordEnabled || magicLinkEnabled) && (
                    <form onSubmit={submit} className="space-y-5">
                        <InputField
                            label={t("partner.auth.work_email")}
                            name="email"
                            type="email"
                            value={data.email}
                            autoComplete="username"
                            autoFocus
                            onChange={(e) => setData("email", e.target.value)}
                            error={errors.email}
                        />

                        {magicMode || !passwordEnabled ? (
                            <p className="text-sm text-muted-foreground">{t("partner.auth.magic_hint")}</p>
                        ) : (
                            <>
                                <InputField
                                    label={t("partner.auth.password")}
                                    name="password"
                                    type="password"
                                    value={data.password}
                                    autoComplete="current-password"
                                    onChange={(e) => setData("password", e.target.value)}
                                    error={errors.password}
                                    labelExtra={
                                        <Link href={route("partner.password.request")} className="text-xs text-brand-accent-text hover:underline">
                                            {t("partner.auth.forgot")}
                                        </Link>
                                    }
                                />

                                <CheckboxField
                                    id="remember"
                                    name="remember"
                                    label={t("partner.auth.remember")}
                                    checked={data.remember}
                                    onCheckedChange={(checked) => setData("remember", checked)}
                                />
                            </>
                        )}

                        <Button className="h-11 w-full gap-2" disabled={processing}>
                            {magicMode || !passwordEnabled ? t("partner.auth.send_magic_link") : t("partner.auth.sign_in")}
                            <ArrowRight className="h-4 w-4" aria-hidden />
                        </Button>
                    </form>
                )}

                {!microsoftEnabled && !passwordEnabled && !magicLinkEnabled && (
                    <p className="text-center text-sm text-muted-foreground">{t("auth.no_signin_methods")}</p>
                )}


                {signupEnabled && (
                    <p className="text-center text-sm text-muted-foreground">
                        {t("partner.signup.no_account")}{" "}
                        <Link href={route("partner.signup")} className="font-medium text-brand-accent-text hover:underline">
                            {t("partner.signup.sign_up")}
                        </Link>
                    </p>
                )}

                <div className="flex items-center justify-between border-t border-border pt-4 text-xs">
                    <span className="text-muted-foreground">{t("partner.auth.not_partner")}</span>
                    <Link href={route("partner.access-request")} className="text-brand-accent-text hover:underline">
                        {t("partner.auth.request_access")}
                    </Link>
                </div>
            </div>
        </PartnerAuthLayout>
    );
}
